// Enquiry to Invoice (backend routes mounted at /api/e2i). The shapes below are the API contract shared with the
// backend; the label maps at the end are the screens' own.

export type RegisterCode = 10 | 12 | 13 | 15 | 16;

export interface PartyRef { code: string; name: string }

export type EnquiryType = 'OneTimeCallout' | 'CalloutExistingContract' | 'AnnualContract' | 'CalloutWithoutQuotation';
export type EnquiryStatus = 'Draft' | 'Posted' | 'Cancelled';
export type EstimateStatus = 'Draft' | 'Posted' | 'Superseded';
export type QuotationStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Sent' | 'ClientApproved' | 'ClientRejected' | 'Superseded' | 'Cancelled';
export type JobKind = 'CAE' | 'CAL';
export type JobStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'OrderDrafted' | 'Ordered' | 'Completed' | 'Cancelled';
export type CirStatus = 'Open' | 'Submitted' | 'Booked' | 'Posted' | 'Cancelled';
export type DocMode = 'Item' | 'Service';

export interface CostType { code: string; name: string; markup: number }
export interface VatCodeOption { code: string; name: string; rate: number }

export interface E2IConfig {
  companyId: number;
  enabled: boolean;
  currency: string | null;
  vatRegistered: boolean;
  capabilities: {
    admin: boolean; viewAll: boolean; estimate: boolean; quote: boolean; jobs: boolean;
    finance: boolean; register: boolean; setup: boolean;
  };
  costTypes: CostType[];
  profitPct: number;
  vatCodes: VatCodeOption[];
  defaultVatCode: string | null;
  jobTypes: Array<{ code: string; name: string }>;
  paymentTerms: Array<{ code: number; name: string }>;
  registerCodes: Array<{ code: RegisterCode; name: string }>;
  quoteValidityDays: number;
  docMode: DocMode;
}

export interface EnquiryRow {
  enquiryId: number;
  enquiryNo: string | null;
  enquiryDate: string;
  customer: PartyRef;
  project: PartyRef | null;
  enquiryType: string;
  subject: string;
  status: EnquiryStatus;
  registerStatus: RegisterCode | null;
  registerName: string | null;
  latestQuotation: { quotationNo: string | null; revision: number; status: string; total: number } | null;
  jobNo: string | null;
  invoicedTotal: number;
  updatedAt: string;
}

export interface EnquiryActivity { lineNum: number; activity: string; description: string | null }

export interface Enquiry {
  enquiryId: number;
  enquiryNo: string | null;
  companyId: number;
  enquiryDate: string;
  quoteRequiredDate: string | null;
  customer: PartyRef & { federalTaxId: string | null };
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  project: PartyRef | null;
  enquiryType: EnquiryType;
  subject: string;
  remarks: string | null;
  status: EnquiryStatus;
  registerStatus: RegisterCode | null;
  registerName: string | null;
  cancelReason: string | null;
  activities: EnquiryActivity[];
  createdByName: string | null;
  createdAt: string;
  postedAt: string | null;
}

export interface EstimateLine {
  lineNum: number;
  costType: string;
  itemCode: string | null;
  description: string;
  quantity: number;
  uom: string | null;
  unitCost: number;
  amount: number;
}

export interface Estimate {
  estimateId: number;
  estimateNo: string | null;
  revision: number;
  status: EstimateStatus;
  remarks: string | null;
  totalCost: number;
  lines: EstimateLine[];
  postedAt: string | null;
  createdByName: string | null;
}

export interface QuotationLine {
  lineNum: number;
  /** null: a priced line (typed price, merged lines); else the price is unit cost x markup x profit. */
  costType: string | null;
  description: string;
  quantity: number;
  uom: string | null;
  unitCost: number;
  markupPct: number;
  unitPrice: number;
  amount: number;
  salesItem: string | null;
  account: string | null;
}

export interface ApprovalStage {
  stepNumber: number;
  name: string;
  role: string;
  status: 'Done' | 'Current' | 'Waiting' | 'Skipped';
  approvers: Array<{ name: string; status: string; decidedAt: string | null }>;
}

export interface ApprovalView { stages: ApprovalStage[]; currentStep: number | null }

export interface SapDocState { status: 'Posting' | 'Posted' | 'Failed'; docEntry: number | null; docNum: string | null; error: string | null }

export interface Quotation {
  quotationId: number;
  quotationNo: string | null;
  revision: number;
  estimateId: number | null;
  status: QuotationStatus;
  markups: Record<string, number>;
  profitPct: number;
  addChargePct: number | null;
  addChargeAmount: number | null;
  addChargeReason: string | null;
  discountPct: number | null;
  discountAmount: number | null;
  vatCode: string | null;
  vatRate: number;
  subtotal: number;
  vatAmount: number;
  total: number;
  validUntil: string | null;
  terms: string | null;
  scope: string | null;
  clientRef: string | null;
  lines: QuotationLine[];
  approval: ApprovalView;
  sap: SapDocState | null;
  sentAt: string | null;
  client: { decision: 'approved' | 'rejected'; at: string; poRef: string | null; poDate: string | null; note: string | null } | null;
  preparedByName: string | null;
}

export interface JobLine {
  lineId: number;
  lineNum: number;
  description: string;
  quantity: number;
  uom: string | null;
  unitPrice: number;
  amount: number;
  salesItem: string | null;
  account: string | null;
  sapLine: number | null;
  cirNo: string | null;
  invoiced: boolean;
}

export interface Job {
  jobId: number;
  jobNo: string | null;
  kind: JobKind;
  calReason: string | null;
  quotationId: number | null;
  description: string;
  jobType: string | null;
  location: string | null;
  costCenter: string | null;
  engineer: { userId: number; name: string } | null;
  paymentTerms: number | null;
  contractStart: string | null;
  contractEnd: string | null;
  autoRenew: boolean;
  gracePeriodDays: number | null;
  remarks: string | null;
  project: PartyRef | null;
  status: JobStatus;
  approval: ApprovalView;
  /** draftNum: the number SAP shows on the draft; draftEntry: its key. */
  sap: (SapDocState & { draftEntry: number | null; draftNum?: string | null }) | null;
  lines: JobLine[];
  total: number;
  invoicedTotal: number;
  completedAt: string | null;
}

export interface CirLine { lineNum: number; jobLineId: number; description: string; amount: number }

export interface Attachment { attachmentId: number; fileName: string; size: number | null; uploadedAt: string; uploadedByName: string | null }

export interface CirPermissions {
  canEdit: boolean; canSubmit: boolean; canBook: boolean; canReturn: boolean; canCancel: boolean; canSetClientDate: boolean;
}

export interface Cir {
  cirId: number;
  cirNo: string | null;
  jobId: number;
  jobNo: string | null;
  enquiryId: number;
  enquiryNo: string | null;
  customer: PartyRef;
  customerTrn: string | null;
  customerPoRef: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  remarks: string | null;
  status: CirStatus;
  subtotal: number;
  vatAmount: number;
  total: number;
  lines: CirLine[];
  submittedAt: string | null;
  submittedByName: string | null;
  bookedAt: string | null;
  bookedByName: string | null;
  returnNote: string | null;
  sap: { draftEntry: number | null; draftNum?: string | null; invoiceEntry: number | null; invoiceNum: string | null; invoiceDate: string | null; error: string | null } | null;
  clientSubmissionDate: string | null;
  attachments: Attachment[];
  permissions: CirPermissions;
}

export interface HistoryEntry { at: string; userName: string | null; entity: string; action: string; comment: string | null }

export interface WorkspacePermissions {
  canEdit: boolean; canPost: boolean; canCancel: boolean; canEstimate: boolean; canQuote: boolean;
  canDecideQuotation: boolean; canSendQuotation: boolean; canRecordClientDecision: boolean;
  canCreateJob: boolean; canDecideJob: boolean; canCreateOrder: boolean; canCompleteJob: boolean;
  canCreateCir: boolean; canSetRegister: boolean;
}

export interface WorkspaceCheck { level: 'error' | 'warning'; code: string; message: string }

export interface EnquiryWorkspace {
  enquiry: Enquiry;
  estimates: Estimate[];
  quotations: Quotation[];
  jobs: Job[];
  cirs: Cir[];
  attachments: Attachment[];
  history: HistoryEntry[];
  permissions: WorkspacePermissions;
  checks: WorkspaceCheck[];
}

export interface RegisterRow {
  enquiryId: number;
  enquiryNo: string | null;
  enquiryDate: string;
  customer: PartyRef;
  project: PartyRef | null;
  subject: string;
  estimateNo: string | null;
  quotationNo: string | null;
  quotationRevision: number | null;
  quotationDate: string | null;
  quotationTotal: number | null;
  registerStatus: RegisterCode | null;
  registerName: string | null;
  registerUpdatedAt: string | null;
  jobNo: string | null;
  salesOrderNum: string | null;
  contractStart: string | null;
  contractEnd: string | null;
  clientPoRef: string | null;
  invoicedTotal: number;
  pendingTotal: number;
  invoiceNums: string[];
  lastClientSubmission: string | null;
}

export interface JobTypeSetting { code: string; name: string; salesItem: string | null; account: string | null }
export interface QuotationStageSetting { name: string; role: string; minAmount: number | null }
export interface CalStageSetting { name: string; role: string }

export interface E2ISettings {
  costTypes: CostType[];
  profitPct: number;
  vatCode: string | null;
  quoteValidityDays: number;
  terms: string | null;
  jobTypes: JobTypeSetting[];
  quotationStages: QuotationStageSetting[];
  calStages: CalStageSetting[];
  financeRole: string | null;
  docMode: DocMode;
  defaultSalesItem: string | null;
  defaultRevenueAccount: string | null;
  branchId: number | null;
  dim5SalesItem: boolean;
  postQuotations: boolean;
}

export interface E2ISetupData { settings: E2ISettings; roles: string[] }

// ---------------------------------------------------------------- lookups

export interface CustomerOption extends PartyRef { federalTaxId: string | null }
export interface ProjectOption extends PartyRef { parent: string | null; type: string | null; warehouse: string | null }
export interface UserOption { userId: number; name: string }

// ---------------------------------------------------------------- request bodies

export interface EnquiryInput {
  enquiryDate: string;
  quoteRequiredDate: string | null;
  customerCode: string;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  projectCode: string | null;
  enquiryType: EnquiryType;
  subject: string;
  remarks: string | null;
  activities: EnquiryActivity[];
}

export interface EstimateSaveInput { remarks: string | null; lines: EstimateLine[] }

export interface QuotationSaveInput {
  markups: Record<string, number>;
  profitPct: number;
  addChargePct: number | null;
  addChargeAmount: number | null;
  addChargeReason: string | null;
  discountPct: number | null;
  discountAmount: number | null;
  vatCode: string | null;
  validUntil: string | null;
  terms: string | null;
  scope: string | null;
  clientRef: string | null;
  lines: QuotationLine[];
}

export interface JobFieldsInput {
  description: string;
  jobType: string | null;
  location: string | null;
  costCenter: string | null;
  engineerUserId: number | null;
  paymentTerms: number | null;
  contractStart: string | null;
  contractEnd: string | null;
  autoRenew: boolean;
  gracePeriodDays: number | null;
  remarks: string | null;
  projectCode: string | null;
}

export interface JobLineInput {
  lineNum: number;
  description: string;
  quantity: number;
  uom: string | null;
  unitPrice: number;
  amount: number;
  salesItem: string | null;
  account: string | null;
}

/** A job without a quotation (CAL): the reason and the typed lines. */
export interface CalJobInput extends JobFieldsInput { kind: 'CAL'; calReason: string; lines: JobLineInput[] }

/** PUT /jobs/:id: the fields, and for a CAL job its reason and lines. */
export interface JobSaveInput extends JobFieldsInput { calReason?: string; lines?: JobLineInput[] }

export interface CirSaveInput {
  customerTrn: string | null;
  customerPoRef: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  remarks: string | null;
  jobLineIds: number[];
}

export interface BulkSubmitResult { submitted: number[]; failed: Array<{ cirId: number; message: string }> }

export interface EnquiryFilters { status?: string; register?: string | number; q?: string; from?: string; to?: string }
export interface RegisterFilters { from?: string; to?: string; status?: string | number; customer?: string; project?: string; q?: string }

/** Files are kept per enquiry and per invoice request. */
export type AttachmentEntity = 'enquiry' | 'cir';

// ---------------------------------------------------------------- labels (screens only)

export type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
export interface ChipStyle { label: string; color: ChipColor; outlined?: boolean }

export const ENQUIRY_TYPES: Array<{ value: EnquiryType; label: string }> = [
  { value: 'OneTimeCallout', label: 'One-time call-out' },
  { value: 'CalloutExistingContract', label: 'Call-out under an existing contract' },
  { value: 'AnnualContract', label: 'Annual contract' },
  { value: 'CalloutWithoutQuotation', label: 'Call-out without quotation' },
];

export const ENQUIRY_STATUS: Record<EnquiryStatus, ChipStyle> = {
  Draft: { label: 'Draft', color: 'default' },
  Posted: { label: 'Posted', color: 'success' },
  Cancelled: { label: 'Cancelled', color: 'default', outlined: true },
};

export const ESTIMATE_STATUS: Record<EstimateStatus, ChipStyle> = {
  Draft: { label: 'Draft', color: 'default' },
  Posted: { label: 'Posted', color: 'success' },
  Superseded: { label: 'Superseded', color: 'default', outlined: true },
};

export const QUOTATION_STATUS: Record<QuotationStatus, ChipStyle> = {
  Draft: { label: 'Draft', color: 'default' },
  PendingApproval: { label: 'In approval', color: 'warning' },
  Approved: { label: 'Approved, not sent', color: 'info', outlined: true },
  Sent: { label: 'Sent to client', color: 'info' },
  ClientApproved: { label: 'Client approved', color: 'success' },
  ClientRejected: { label: 'Client rejected', color: 'error' },
  Superseded: { label: 'Superseded', color: 'default', outlined: true },
  Cancelled: { label: 'Cancelled', color: 'default', outlined: true },
};

export const JOB_STATUS: Record<JobStatus, ChipStyle> = {
  Draft: { label: 'Draft', color: 'default' },
  PendingApproval: { label: 'In approval', color: 'warning' },
  Approved: { label: 'Approved', color: 'info', outlined: true },
  OrderDrafted: { label: 'Sales order draft in SAP', color: 'info' },
  Ordered: { label: 'Sales order in SAP', color: 'success', outlined: true },
  Completed: { label: 'Works completed', color: 'success' },
  Cancelled: { label: 'Cancelled', color: 'default', outlined: true },
};

export const CIR_STATUS: Record<CirStatus, ChipStyle> = {
  Open: { label: 'Open', color: 'default' },
  Submitted: { label: 'Submitted to finance', color: 'warning' },
  Booked: { label: 'Booked as SAP draft', color: 'info' },
  Posted: { label: 'Invoice posted', color: 'success' },
  Cancelled: { label: 'Cancelled', color: 'default', outlined: true },
};

export const SAP_STATE: Record<SapDocState['status'], ChipStyle> = {
  Posting: { label: 'Sending to SAP', color: 'info' },
  Posted: { label: 'In SAP', color: 'success' },
  Failed: { label: 'SAP refused', color: 'error' },
};

/** Horizon's register codes; the names come from the server (config.registerCodes) when it gives them. */
export const REGISTER_NAMES: Record<RegisterCode, string> = {
  10: 'Enquiry open',
  12: 'Quote approved',
  13: 'Works completed',
  15: 'Works invoiced',
  16: 'Cancelled',
};

/** Register chip colours: dark text on a light tint (contrast above 7:1). */
export const REGISTER_COLORS: Record<RegisterCode, { bg: string; fg: string }> = {
  10: { bg: '#e3f2fd', fg: '#0d3c78' },
  12: { bg: '#ede7f6', fg: '#3f2a8c' },
  13: { bg: '#fff3e0', fg: '#7a3e00' },
  15: { bg: '#e8f5e9', fg: '#1b5e20' },
  16: { bg: '#eceff1', fg: '#37474f' },
};

export const REGISTER_CODES: RegisterCode[] = [10, 12, 13, 15, 16];
